import React from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Paper,
  Alert,
  Chip,
  InputAdornment,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CloudQueue as CloudIcon,
  Air as AirIcon,
  WbTwilight as TwilightIcon,
  Schedule as ScheduleIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as AccessTimeIcon,
  Tune as CalibrationIcon,
} from '@mui/icons-material';
import { Controller } from 'react-hook-form';
import DurationEstimate from './DurationEstimate';
import astronomyService, { LUNAR_CYCLE_DAYS, FULL_MOON_DAYS } from '../../../services/astronomyService';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const ConstraintsStep = ({ control, errors, watch, setValue }) => {
  const formData = watch();

  // #937. 'fill' preserves the pre-#937 behaviour (a required end time, fillTime owned by the old
  // switch); 'exposures' is the new pinned-start mode. Defaulted here rather than via a Controller
  // defaultValue so a conditional remount can never clobber a hydrated value on the edit page.
  const fixedTimeMode = watch('fixedTimeMode') ?? (watch('fillTime') === true ? 'fill' : 'exposures');
  const isFillMode = fixedTimeMode === 'fill';
  
  console.log('ConstraintsStep formData:', formData);
  console.log('ConstraintsStep errors:', errors);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Observation Constraints
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Set the conditions under which your observation should be executed. These constraints ensure optimal data quality.
      </Typography>

      <Grid container spacing={3}>
        {/* Altitude Constraints */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VisibilityIcon color="primary" />
              Altitude Constraints
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography gutterBottom>Minimum Altitude</Typography>
                <Controller
                  name="minAltitude"
                  control={control}
                  defaultValue={30}
                  render={({ field }) => (
                    <Box sx={{ px: 2 }}>
                      <Slider
                        {...field}
                        marks
                        min={0}
                        max={90}
                        step={5}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value}°`}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption">0° (Horizon)</Typography>
                        <Typography variant="caption">90° (Zenith)</Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography gutterBottom>Airmass Range</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Controller
                      name="minAirmass"
                      control={control}
                      defaultValue={1.0}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Minimum"
                          type="number"
                          size="small"
                          InputProps={{ inputProps: { min: 1.0, max: 3.0, step: 0.1 } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="maxAirmass"
                      control={control}
                      defaultValue={2.0}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Maximum"
                          type="number"
                          size="small"
                          InputProps={{ inputProps: { min: 1.0, max: 3.0, step: 0.1 } }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography gutterBottom>Hour Angle Limits</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Controller
                      name="minHourAngle"
                      control={control}
                      defaultValue={-5.0}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="West Limit"
                          type="number"
                          size="small"
                          InputProps={{ inputProps: { min: -12, max: 0, step: 0.5 } }}
                          helperText="Hours west of meridian"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="maxHourAngle"
                      control={control}
                      defaultValue={5.0}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="East Limit"
                          type="number"
                          size="small"
                          InputProps={{ inputProps: { min: 0, max: 12, step: 0.5 } }}
                          helperText="Hours east of meridian"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Controller
                  name="horizonElevation"
                  control={control}
                  defaultValue={15}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Horizon Elevation (degrees)"
                      type="number"
                      InputProps={{ inputProps: { min: 0, max: 90 } }}
                      helperText="Custom horizon limit for this observation"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Moon Constraints */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TwilightIcon color="primary" />
              Moon Constraints
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Moon avoidance uses the Lorentzian model: you set the separation required at
                  <strong> full moon</strong>, and the requirement falls away towards new moon.
                  Sensitivity controls how quickly it falls off. These are the same two settings
                  the target search uses, so a target that passes the search will pass the
                  scheduler.
                </Alert>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="moonSeparation"
                  control={control}
                  defaultValue={60}
                  rules={{
                    min: { value: 0, message: 'Separation must be at least 0°' },
                    max: { value: 180, message: 'Separation cannot exceed 180°' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Separation at Full Moon (degrees)"
                      type="number"
                      InputProps={{
                        inputProps: { min: 0, max: 180, step: 5 },
                        endAdornment: <Typography variant="caption">°</Typography>
                      }}
                      helperText="Maximum requirement, applied at full moon"
                      error={!!errors.moonSeparation}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="moonAvoidanceWidth"
                  control={control}
                  defaultValue={5}
                  rules={{
                    min: { value: 1, message: 'Sensitivity must be at least 1 day' },
                    max: { value: 10, message: 'Sensitivity cannot exceed 10 days' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Moon Sensitivity (days)"
                      type="number"
                      InputProps={{
                        inputProps: { min: 1, max: 10, step: 1 },
                        endAdornment: <Typography variant="caption">days</Typography>
                      }}
                      helperText={
                        `${Number(watch('moonAvoidanceWidth')) <= 3 ? 'Low' :
                          Number(watch('moonAvoidanceWidth')) <= 7 ? 'Medium' : 'High'}` +
                        ' — how many days either side of full moon the requirement stays high'
                      }
                      error={!!errors.moonAvoidanceWidth}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="moonDown"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Moon must be below horizon (overrides Lorentzian)"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography gutterBottom>Maximum Moon Phase</Typography>
                <Controller
                  name="maxMoonPhase"
                  control={control}
                  defaultValue={100}
                  render={({ field }) => (
                    <Box sx={{ px: 2 }}>
                      <Slider
                        {...field}
                        marks={[
                          { value: 0, label: 'New' },
                          { value: 50, label: 'Quarter' },
                          { value: 100, label: 'Full' },
                        ]}
                        min={0}
                        max={100}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value}%`}
                      />
                    </Box>
                  )}
                />
              </Grid>
            </Grid>

            {/* Visual representation of the calculated requirement across the lunar cycle */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Separation required at each moon phase:
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {(() => {
                  // #1157: call the shared helper rather than re-implementing the formula inline.
                  // This block used to carry its own copy, with its own hardcoded centre of 14 and
                  // its own fixed width, making it a seventh implementation that could disagree
                  // with the scheduler that actually enforces the constraint.
                  const peak = Number(watch('moonSeparation'));
                  const width = Number(watch('moonAvoidanceWidth'));
                  const safePeak = Number.isFinite(peak) ? peak : 60;
                  const safeWidth = Number.isFinite(width) && width > 0 ? width : 5;

                  const phases = [
                    { name: 'New Moon', day: 0 },
                    { name: 'First Quarter', day: LUNAR_CYCLE_DAYS / 4 },
                    { name: 'Full Moon', day: FULL_MOON_DAYS },
                    { name: 'Last Quarter', day: (3 * LUNAR_CYCLE_DAYS) / 4 },
                  ];

                  return phases.map((phase) => (
                    <Grid item xs={6} sm={3} key={phase.name}>
                      <Typography variant="caption" display="block" fontWeight="medium">
                        {phase.name}
                      </Typography>
                      <Typography variant="body2">
                        {Math.round(astronomyService.calculateLorentzianMoonSeparation(
                          phase.day, safePeak, safeWidth))}°
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Day {phase.day.toFixed(1)}
                      </Typography>
                    </Grid>
                  ));
                })()}
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Weather Constraints */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudIcon color="primary" />
              Weather Constraints
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="maxClouds"
                  control={control}
                  defaultValue={30}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Maximum Cloud Cover (%)"
                      type="number"
                      InputProps={{ inputProps: { min: 0, max: 100 } }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="maxHumidity"
                  control={control}
                  defaultValue={80}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Maximum Humidity (%)"
                      type="number"
                      InputProps={{ inputProps: { min: 0, max: 100 } }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="maxWind"
                  control={control}
                  defaultValue={30}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Maximum Wind Speed (km/h)"
                      type="number"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  )}
                />
              </Grid>
            </Grid>
            
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <Controller
                  name="skyCondition"
                  control={control}
                  defaultValue="good"
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Sky Condition</InputLabel>
                      <Select {...field} label="Sky Condition">
                        <MenuItem value="good">Good</MenuItem>
                        <MenuItem value="acceptable">Acceptable</MenuItem>
                        <MenuItem value="poor">Poor</MenuItem>
                        <MenuItem value="any">Any</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Timing Constraints */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon color="primary" />
              Timing Constraints
            </Typography>
            
            <Grid container spacing={2}>
              {/* Observation Type Selector */}
              <Grid item xs={12}>
                <Controller
                  name="observationType"
                  control={control}
                  defaultValue="flexible"
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Observation Type</InputLabel>
                      <Select {...field} label="Observation Type">
                        <MenuItem value="flexible">Flexible - Schedule optimally within date range</MenuItem>
                        <MenuItem value="time_based">Time-Based - Run for a specified duration</MenuItem>
                        <MenuItem value="fixed_time">Fixed Time - Must execute at specific time</MenuItem>
                        <MenuItem value="rise_to_set">Rise to Set - Observe from rise to set</MenuItem>
                        <MenuItem value="monitoring">Monitoring - Repeat on regular cadence</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Show different fields based on observation type */}
              {watch('observationType') === 'rise_to_set' ? (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<AccessTimeIcon />}>
                      Rise-to-set observations automatically calculate the observation window from when the target
                      rises above the minimum altitude until it sets, respecting twilight constraints.
                      The exposure sequence will repeat continuously throughout the entire window.
                      Perfect for maximizing coverage of asteroids, variable stars, or any target you want to observe all night.
                    </Alert>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Controller
                        name="riseToSetDate"
                        control={control}
                        defaultValue={null}
                        rules={{
                          required: watch('observationType') === 'rise_to_set' ? 'Observation date is required' : false
                        }}
                        render={({ field }) => (
                          <DatePicker
                            {...field}
                            label="Observation Date (UTC) *"
                            format="yyyy-MM-dd"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.riseToSetDate,
                                helperText: errors.riseToSetDate?.message || "Date for the observation (times will be calculated)",
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="riseToSetMinAltitude"
                      control={control}
                      defaultValue={30}
                      rules={{
                        min: { value: 15, message: 'Minimum altitude must be at least 15°' },
                        max: { value: 60, message: 'Minimum altitude cannot exceed 60°' }
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Minimum Altitude (degrees)"
                          type="number"
                          error={!!errors.riseToSetMinAltitude}
                          helperText={errors.riseToSetMinAltitude?.message || "Target must be above this altitude"}
                          InputProps={{
                            inputProps: { min: 15, max: 60, step: 5 },
                            endAdornment: <InputAdornment position="end">°</InputAdornment>
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="riseToSetTwilightType"
                      control={control}
                      defaultValue="astronomical"
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Twilight Type</InputLabel>
                          <Select {...field} label="Twilight Type">
                            <MenuItem value="astronomical">Astronomical (-18°)</MenuItem>
                            <MenuItem value="nautical">Nautical (-12°)</MenuItem>
                            <MenuItem value="civil">Civil (-6°)</MenuItem>
                          </Select>
                          <FormHelperText>Don't observe during twilight</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="riseToSetSafetyMargin"
                      control={control}
                      defaultValue={0}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Safety Margin (minutes)"
                          type="number"
                          helperText="Time buffer before rise and after set"
                          InputProps={{ inputProps: { min: 0, max: 30, step: 5 } }}
                        />
                      )}
                    />
                  </Grid>
                  {watch('riseToSetDate') && watch('targetName') && watch('ra') && watch('dec') && watch('assignedTo') && (
                    <Grid item xs={12}>
                      <Paper elevation={2} sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          📅 Calculated Observation Window
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Window will be calculated when you review the observation.
                          The observation will run from target rise to set, respecting your twilight and altitude constraints.
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </>
              ) : watch('observationType') === 'time_based' ? (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<AccessTimeIcon />}>
                      Time-based observations will repeat their exposure plan to fill the specified duration. 
                      The scheduler can interrupt after the minimum time if needed for higher priority observations.
                    </Alert>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="minimumObservingTimeMinutes"
                      control={control}
                      defaultValue={30}
                      rules={{
                        required: watch('observationType') === 'time_based' ? 'Minimum time is required' : false,
                        min: { value: 1, message: 'Minimum time must be at least 1 minute' }
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Minimum Observing Time (minutes) *"
                          type="number"
                          error={!!errors.minimumObservingTimeMinutes}
                          helperText={errors.minimumObservingTimeMinutes?.message || "Observation must run for at least this long"}
                          InputProps={{ inputProps: { min: 1, step: 5 } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="desiredObservingTimeMinutes"
                      control={control}
                      defaultValue={60}
                      rules={{
                        required: watch('observationType') === 'time_based' ? 'Desired time is required' : false,
                        min: { value: 1, message: 'Desired time must be at least 1 minute' },
                        validate: (value) => {
                          if (watch('observationType') === 'time_based' && value && watch('minimumObservingTimeMinutes')) {
                            return value >= watch('minimumObservingTimeMinutes') || 'Desired time must be at least the minimum time';
                          }
                          return true;
                        }
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Desired Observing Time (minutes) *"
                          type="number"
                          error={!!errors.desiredObservingTimeMinutes}
                          helperText={errors.desiredObservingTimeMinutes?.message || "Observation can run up to this long if time permits"}
                          InputProps={{ inputProps: { min: 1, step: 5 } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="fillTime"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              {...field}
                              checked={field.value || false}
                            />
                          }
                          label="Fill allocated time with unlimited exposures"
                        />
                      )}
                    />
                    <FormHelperText>
                      When enabled, ignores exposure counts and runs continuously until allocated time expires.
                      When disabled (default), uses specified exposure counts and cycles through the sequence until time expires.
                    </FormHelperText>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      The exposure plan will be repeated as many times as needed to fill the observing time.
                      The plugin will track actual observing time and stop cleanly after completing a full exposure plan
                      once the minimum time is reached.
                    </Typography>
                  </Grid>
                </>
              ) : watch('observationType') === 'fixed_time' ? (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<AccessTimeIcon />}>
                      Fixed-time observations start at a time you choose.
                      They are ideal for transits, occultations, or coordinated observations.
                      <br /><strong>Note: Enter times in UTC (24-hour format). The calendar shows UTC, not local time.</strong>
                    </Alert>
                  </Grid>

                  {/*
                    #937: the mode owns `fillTime` and whether an end time is required.
                    Deliberately NOT a <Controller>: a Controller's `defaultValue` clobbers hydrated
                    state when this branch conditionally mounts on the edit page (see the warning in
                    ObservationEdit.js). Same watch/setValue pattern as AutofocusStep's `useDefaults`.
                    Deriving fillTime here covers all three payload builders from one place.
                  */}
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ mb: 0.5 }}>How should this observation use its time?</FormLabel>
                      <RadioGroup
                        value={fixedTimeMode}
                        onChange={(e) => {
                          const mode = e.target.value;
                          setValue('fixedTimeMode', mode);
                          setValue('fillTime', mode === 'fill');
                          if (mode === 'exposures') {
                            // The window is auto-sized from the estimate; a stale user-typed end
                            // would otherwise survive and keep reserving the padded window.
                            setValue('fixedTimeEnd', null);
                          }
                        }}
                      >
                        <FormControlLabel
                          value="exposures"
                          control={<Radio />}
                          label="Fixed start — just my exposures"
                        />
                        <FormHelperText sx={{ mt: -1, ml: 4 }}>
                          Starts at your time, runs only as long as your exposure plan needs, then
                          releases the telescope. No end time required — we size the window for you.
                        </FormHelperText>
                        <FormControlLabel
                          value="fill"
                          control={<Radio />}
                          label="Fill the window"
                          sx={{ mt: 1 }}
                        />
                        <FormHelperText sx={{ mt: -1, ml: 4 }}>
                          Runs continuously from start to end, ignoring exposure counts. Reserves the
                          whole window, so nothing else can be scheduled in it.
                        </FormHelperText>
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Controller
                        name="fixedTimeStart"
                        control={control}
                        defaultValue={null}
                        rules={{
                          required: watch('observationType') === 'fixed_time' ? 'Start time is required for fixed-time observations' : false
                        }}
                        render={({ field: { onChange, value, ...field } }) => {
                          // Display: convert UTC Date to "fake local" so picker shows UTC values
                          let displayValue = null;
                          if (value) {
                            const d = new Date(value);
                            // #1414: guard against an Invalid Date reaching the picker (crashes MUI
                            // with "Invalid time value"); an unparseable value stays null.
                            if (!Number.isNaN(d.getTime())) {
                              displayValue = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes());
                            }
                          }
                          return (
                          <DateTimePicker
                            {...field}
                            value={displayValue}
                            onChange={(newValue) => {
                              if (newValue && !isNaN(newValue.getTime())) {
                                // Picker returns local Date but user entered UTC values
                                // Convert "fake local" back to real UTC
                                const newStart = new Date(Date.UTC(newValue.getFullYear(), newValue.getMonth(), newValue.getDate(), newValue.getHours(), newValue.getMinutes()));
                                // Shift end time by the same delta to preserve duration.
                                // #937: skip in pinned-start mode — there is no user-owned end to
                                // preserve, and the server re-derives it from the start anyway.
                                const oldStart = watch('fixedTimeStart');
                                const endTime = watch('fixedTimeEnd');
                                if (isFillMode && oldStart && endTime) {
                                  const delta = newStart.getTime() - new Date(oldStart).getTime();
                                  setValue('fixedTimeEnd', new Date(new Date(endTime).getTime() + delta));
                                }
                                onChange(newStart);
                              } else {
                                onChange(null);
                              }
                            }}
                            label="Fixed Start Time (UTC) *"
                            format="yyyy-MM-dd HH:mm"
                            ampm={false}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.fixedTimeStart,
                                helperText: errors.fixedTimeStart?.message || "Enter time in UTC (24-hour format)",
                              },
                            }}
                          />
                          );
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Controller
                        name="fixedTimeEnd"
                        control={control}
                        defaultValue={null}
                        rules={{
                          // #937: only "fill the window" needs an end time. A pinned-start
                          // observation has its window sized from the duration estimate.
                          required: (watch('observationType') === 'fixed_time' && isFillMode)
                            ? 'End time is required when filling the window'
                            : false,
                          validate: (value) => {
                            if (watch('observationType') === 'fixed_time' && value && watch('fixedTimeStart')) {
                              return value > watch('fixedTimeStart') || 'End time must be after start time';
                            }
                            return true;
                          }
                        }}
                        render={({ field: { onChange, value, ...field } }) => {
                          // Display: convert UTC Date to "fake local" so picker shows UTC values
                          let displayValue = null;
                          if (value) {
                            const d = new Date(value);
                            // #1414: guard against an Invalid Date reaching the picker (crashes MUI
                            // with "Invalid time value"); an unparseable value stays null.
                            if (!Number.isNaN(d.getTime())) {
                              displayValue = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes());
                            }
                          }
                          return (
                          <DateTimePicker
                            {...field}
                            value={displayValue}
                            onChange={(newValue) => {
                              if (newValue && !isNaN(newValue.getTime())) {
                                // Picker returns local Date but user entered UTC values
                                // Convert "fake local" back to real UTC
                                onChange(new Date(Date.UTC(newValue.getFullYear(), newValue.getMonth(), newValue.getDate(), newValue.getHours(), newValue.getMinutes())));
                              } else {
                                onChange(null);
                              }
                            }}
                            label={isFillMode ? "Fixed End Time (UTC) *" : "Fixed End Time (UTC) — optional deadline"}
                            format="yyyy-MM-dd HH:mm"
                            ampm={false}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.fixedTimeEnd,
                                helperText: errors.fixedTimeEnd?.message || (isFillMode
                                  ? "Enter time in UTC (24-hour format)"
                                  : "Leave blank and we'll size the window from the estimate below. Set one only to impose a deadline."),
                              },
                            }}
                          />
                          );
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  {/*
                    #937: `fillTime` is now derived from the mode selector above, so the old
                    standalone switch is gone. It stays in the payload; only the control is retired.
                    Instead we show what the reporter of SUP-2026-00005 actually asked for: how long
                    the observation will take, and that plate-solving and autofocus consume the window.
                  */}
                  {!isFillMode && (
                    <Grid item xs={12}>
                      <DurationEstimate
                        targets={formData.targets}
                        assignedTo={formData.assignedTo}
                        globalRepeatCount={formData.globalRepeatCount}
                        // `useDefaults` (AutofocusStep) means "inherit the observatory settings", so
                        // send nothing and let the server resolve them. Otherwise pass the overrides:
                        // the initial autofocus is the single biggest term in the setup estimate.
                        autofocusOptions={
                          (formData.useDefaults ?? true)
                            ? undefined
                            : { enabled: formData.afEnabled !== false, initial_autofocus: formData.initialAutofocus !== false }
                        }
                        showWindowHint
                      />
                    </Grid>
                  )}

                  {/* Soft pin: 0 keeps a transit ingress exactly on time; larger lets the scheduler
                      fit other users' work around this observation. Honored by constraint_solver.py
                      (start bounds) and app.py (dispatch gate). */}
                  {!isFillMode && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="start-tolerance-label">Start tolerance</InputLabel>
                        <Select
                          labelId="start-tolerance-label"
                          label="Start tolerance"
                          value={watch('startToleranceMinutes') ?? 0}
                          onChange={(e) => setValue('startToleranceMinutes', Number(e.target.value))}
                        >
                          <MenuItem value={0}>Exact start (0 min)</MenuItem>
                          <MenuItem value={5}>± 5 minutes</MenuItem>
                          <MenuItem value={15}>± 15 minutes</MenuItem>
                          <MenuItem value={30}>± 30 minutes</MenuItem>
                          <MenuItem value={60}>± 1 hour</MenuItem>
                        </Select>
                        <FormHelperText>
                          How far the scheduler may shift your start time to fit other observations
                          around yours. Use “Exact start” for a transit ingress.
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  )}
                </>
              ) : watch('observationType') === 'monitoring' ? (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<CalendarIcon />}>
                      Monitoring observations repeat automatically on a regular schedule.
                      Perfect for tracking variable stars, asteroids, or long-term projects.
                    </Alert>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="cadenceDays"
                      control={control}
                      defaultValue={1}
                      rules={{
                        required: watch('observationType') === 'monitoring' ? 'Cadence is required' : false,
                        min: { value: 0.5, message: 'Minimum cadence is 0.5 days (12 hours)' }
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Repeat Every (days)"
                          type="number"
                          error={!!errors.cadenceDays}
                          helperText={errors.cadenceDays?.message || "How often to repeat the observation"}
                          InputProps={{ inputProps: { min: 0.5, step: 0.5 } }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="cadenceMaxObservations"
                      control={control}
                      defaultValue={null}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Maximum Observations (Optional)"
                          type="number"
                          error={!!errors.cadenceMaxObservations}
                          helperText={errors.cadenceMaxObservations?.message || "Stop after this many observations"}
                          InputProps={{ inputProps: { min: 1, step: 1 } }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Controller
                        name="cadenceStartDate"
                        control={control}
                        defaultValue={null}
                        rules={{
                          required: watch('observationType') === 'monitoring' ? 'Start date is required' : false
                        }}
                        render={({ field: { onChange, value, ...field } }) => (
                          <DatePicker
                            {...field}
                            value={value || null}
                            onChange={(newValue) => onChange(newValue)}
                            label="Monitoring Start Date"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: watch('observationType') === 'monitoring',
                                error: !!errors.cadenceStartDate,
                                helperText: errors.cadenceStartDate?.message || "When to begin monitoring",
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Controller
                        name="cadenceEndDate"
                        control={control}
                        defaultValue={null}
                        rules={{
                          validate: (value) => {
                            if (watch('observationType') === 'monitoring' && value && watch('cadenceStartDate')) {
                              return value > watch('cadenceStartDate') || 'End date must be after start date';
                            }
                            return true;
                          }
                        }}
                        render={({ field: { onChange, value, ...field } }) => (
                          <DatePicker
                            {...field}
                            value={value || null}
                            onChange={(newValue) => onChange(newValue)}
                            label="Monitoring End Date (Optional)"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.cadenceEndDate,
                                helperText: errors.cadenceEndDate?.message || "When to stop monitoring",
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Controller
                        name="startTime"
                        control={control}
                        defaultValue={null}
                        render={({ field: { onChange, value, ...field } }) => (
                          <DatePicker
                            {...field}
                            value={value || null}
                            onChange={(newValue) => onChange(newValue)}
                            label="Earliest Date (Optional)"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.startTime,
                                helperText: errors.startTime?.message || "Observation can start on or after this date",
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Controller
                        name="endTime"
                        control={control}
                        defaultValue={null}
                        render={({ field: { onChange, value, ...field } }) => (
                          <DatePicker
                            {...field}
                            value={value || null}
                            onChange={(newValue) => onChange(newValue)}
                            label="Latest Date (Optional)"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.endTime,
                                helperText: errors.endTime?.message || "Observation must complete by this date",
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Controller
                  name="twilightType"
                  control={control}
                  defaultValue="nautical"
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Twilight Requirement</InputLabel>
                      <Select {...field} label="Twilight Requirement">
                        <MenuItem value="none">No requirement</MenuItem>
                        <MenuItem value="civil">Civil Twilight (Sun -6°) - Bright targets</MenuItem>
                        <MenuItem value="nautical">Nautical Twilight (Sun -12°) - Default</MenuItem>
                        <MenuItem value="astronomical">Astronomical Twilight (Sun -18°) - Faint objects</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Additional Options */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Additional Options
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="meridianFlipEnabled"
                  control={control}
                  defaultValue={true}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Allow Meridian Flip"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="autoFocusEnabled"
                  control={control}
                  defaultValue={true}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Auto Focus During Observation"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={2}
                      label="Additional Notes (Optional)"
                      placeholder="Any special requirements or notes for this observation..."
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Fast Mover (NEO) Settings */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon color="primary" />
              Moving Object Settings
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="isFastMover"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label={
                        <Box>
                          <Typography component="span">Fast Mover (NEO)</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Enable for Near Earth Objects, comets, or other objects with rapid apparent motion.
                            Uses fine-grained scheduling (5-minute resolution) instead of the optimized
                            30-minute resolution used for main belt asteroids and stars.
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              For most asteroids (main belt, Trojans, etc.) and all fixed targets, leave this disabled.
              The scheduler automatically detects fast-moving objects, but you can override here if needed.
            </Alert>
          </Paper>
        </Grid>

        {/* Calibration Options */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalibrationIcon color="primary" />
              Calibration
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="calibrationEnabled"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>Calibration</InputLabel>
                      <Select {...field} label="Calibration">
                        <MenuItem value="">Inherit Default</MenuItem>
                        <MenuItem value="true">Enabled</MenuItem>
                        <MenuItem value="false">Disabled</MenuItem>
                      </Select>
                      <FormHelperText>
                        Override calibration setting for this observation. "Inherit" uses your profile or observatory default.
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="rotationMatchFlats"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>Rotation-Matched Flats</InputLabel>
                      <Select {...field} label="Rotation-Matched Flats">
                        <MenuItem value="">Inherit Default</MenuItem>
                        <MenuItem value="required">Required</MenuItem>
                        <MenuItem value="preferred">Preferred</MenuItem>
                        <MenuItem value="ignore">Ignore</MenuItem>
                      </Select>
                      <FormHelperText>
                        Whether flat frames must match the rotator angle. Only relevant if the observatory has a rotator.
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Constraints Summary */}
      <Paper elevation={1} sx={{ p: 2, mt: 3, backgroundColor: 'background.default' }}>
        <Typography variant="subtitle2" gutterBottom>
          Constraints Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            icon={<VisibilityIcon />}
            label={`Alt: ≥${formData.minAltitude || 30}°`}
            variant="outlined"
          />
          <Chip
            size="small"
            icon={<AirIcon />}
            label={`Airmass: ${formData.minAirmass || 1.0}-${formData.maxAirmass || 2.0}`}
            variant="outlined"
          />
          <Chip
            size="small"
            icon={<ScheduleIcon />}
            label={`HA: ${formData.minHourAngle || -5}h to ${formData.maxHourAngle || 5}h`}
            variant="outlined"
          />
          <Chip
            size="small"
            icon={<CloudIcon />}
            label={`Clouds: ≤${formData.maxClouds || 30}%`}
            variant="outlined"
          />
          <Chip
            size="small"
            icon={<TwilightIcon />}
            label={`Moon: ≤${formData.maxMoonPhase || 100}%`}
            variant="outlined"
          />
          {formData.moonDown && (
            <Chip
              size="small"
              icon={<TwilightIcon />}
              label="Moon down"
              variant="outlined"
            />
          )}
          {formData.twilightType && formData.twilightType !== 'none' && (
            <Chip
              size="small"
              icon={<TwilightIcon />}
              label={`${formData.twilightType} twilight`}
              variant="outlined"
            />
          )}
          {/* #937: in pinned-start mode there is no end time to show — surface the pin and its
              tolerance instead, so the chip does not silently disappear. */}
          {formData.observationType === 'fixed_time' && formData.fixedTimeStart && (
            <Chip
              size="small"
              icon={<AccessTimeIcon />}
              label={formData.fixedTimeEnd
                ? `Fixed UTC: ${new Date(formData.fixedTimeStart).toISOString().slice(0, 16).replace('T', ' ')} - ${new Date(formData.fixedTimeEnd).toISOString().slice(0, 16).replace('T', ' ')}`
                : `Starts ${new Date(formData.fixedTimeStart).toISOString().slice(0, 16).replace('T', ' ')} UTC${
                    (formData.startToleranceMinutes ?? 0) > 0 ? ` ±${formData.startToleranceMinutes}m` : ''
                  } · window auto-sized`}
              variant="outlined"
              color="warning"
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ConstraintsStep;